"use client";
import { cn } from "@/lib/utils";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createNoise3D } from "simplex-noise";

interface WavyBackgroundProps {
  className?: string;
  containerClassName?: string;
  colors?: string[];
  waveWidth?: number;
  backgroundFill?: string;
  blur?: number;
  speed?: "slow" | "fast";
  waveOpacity?: number;
}

export const WavyBackground = ({
  children,
  className,
  containerClassName,
  colors: propColors,
  waveWidth,
  backgroundFill,
  blur = 10,
  speed = "fast",
  waveOpacity = 0.5,
  ...props
}: PropsWithChildren<WavyBackgroundProps>) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationIdRef = useRef<number>(0);
  const ntRef = useRef<number>(0);

  // Memoize noise instance
  const noise = useMemo(() => createNoise3D(), []);

  // Memoize speed calculation
  const memoizedSpeed = useCallback(() => {
    switch (speed) {
      case "slow":
        return 0.001;
      case "fast":
        return 0.002;
      default:
        return 0.001;
    }
  }, [speed]);

  // Memoize wave colors
  const currentWaveColors = useMemo(() => {
    return (
      propColors ?? [
        "#0D47A1", // Deep Blue
        "#2E7D32", // Dark Green
        "#00ACC1", // Cyan/Teal
        "#FFB300", // Amber/Gold
        "#263238", // Blue Grey / Dark Slate
      ]
    );
  }, [propColors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.error("Canvas element not found");
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.error("Canvas context not found");
      return;
    }

    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    ctx.filter = `blur(${blur}px)`;
    ntRef.current = 0; // Reset noise time on each effect run

    const handleResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      // Ensure ctx is still valid and filter is reapplied
      if (canvasRef.current) {
        // Check if canvas still exists
        const currentCtx = canvasRef.current.getContext("2d");
        if (currentCtx) {
          currentCtx.filter = `blur(${blur}px)`;
        }
      }
    };
    window.addEventListener("resize", handleResize);

    const drawWave = (n: number) => {
      ntRef.current += memoizedSpeed();
      for (let i = 0; i < n; i++) {
        ctx.beginPath();
        ctx.lineWidth = waveWidth || 50;
        ctx.strokeStyle = currentWaveColors[i % currentWaveColors.length];
        for (let x = 0; x < w; x += 5) {
          var y = noise(x / 800, 0.3 * i, ntRef.current) * 100;
          ctx.lineTo(x, y + h * 0.5); // 0.5 => 50% of the height
        }
        ctx.stroke();
        ctx.closePath();
      }
    };

    const renderCanvas = () => {
      // Ensure ctx is valid before drawing
      if (!canvasRef.current) return; // Stop if canvas is gone
      const currentCtx = canvasRef.current.getContext("2d");
      if (!currentCtx) return;

      currentCtx.fillStyle = backgroundFill || "black";
      currentCtx.globalAlpha = waveOpacity || 0.5;
      currentCtx.fillRect(0, 0, w, h);
      drawWave(5); // drawWave uses the ctx from its closure, which is fine here
      animationIdRef.current = requestAnimationFrame(renderCanvas);
    };

    renderCanvas();

    return () => {
      cancelAnimationFrame(animationIdRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, [
    blur,
    backgroundFill,
    waveOpacity,
    memoizedSpeed,
    currentWaveColors,
    waveWidth,
    noise,
  ]);

  const [isSafari, setIsSafari] = useState(false);
  useEffect(() => {
    setIsSafari(
      typeof window !== "undefined" &&
        navigator.userAgent.includes("Safari") &&
        !navigator.userAgent.includes("Chrome"),
    );
  }, []);

  return (
    <div
      className={cn(
        "relative flex h-full flex-col items-center justify-center",
        containerClassName,
      )}
    >
      <canvas
        className="absolute z-0 h-full w-full"
        ref={canvasRef}
        id="canvas"
        style={{
          ...(isSafari ? { filter: `blur(${blur}px)` } : {}),
        }}
      />
      <div className={cn("relative z-10", className)} {...props}>
        {children}
      </div>
    </div>
  );
};
